import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit'
import PositionService from 'services/crm/PositionService'
import { fetchDeal } from './dealsSlice'

const positionsAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.createdAt < b.createdAt ? 1 : -1),
})

export const fetchPositions = createAsyncThunk(
  'crm/positions/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await PositionService.getAll(params)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching positions')
    }
  },
)

export const fetchPositionsByDeal = createAsyncThunk(
  'crm/positions/fetchByDeal',
  async (dealId, { rejectWithValue }) => {
    try {
      const res = await PositionService.getAll({ dealId })
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching positions')
    }
  },
)

export const createPosition = createAsyncThunk(
  'crm/positions/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const res = await PositionService.create(data)
      // Deal value may have been auto-recalculated on the backend side —
      // pull the fresh deal so the UI reflects the new total.
      if (data.dealId) dispatch(fetchDeal(data.dealId))
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating position')
    }
  },
)

export const updatePosition = createAsyncThunk(
  'crm/positions/update',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await PositionService.update(id, data)
      const dealId = res.data?.dealId
      if (dealId) dispatch(fetchDeal(dealId))
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating position')
    }
  },
)

export const removePosition = createAsyncThunk(
  'crm/positions/remove',
  async ({ id, dealId }, { rejectWithValue, dispatch }) => {
    try {
      await PositionService.remove(id)
      if (dealId) dispatch(fetchDeal(dealId))
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting position')
    }
  },
)

export const movePosition = createAsyncThunk(
  'crm/positions/move',
  async ({ positionId, stageId }, { rejectWithValue }) => {
    try {
      const res = await PositionService.move(positionId, { stageId })
      return res.data
    } catch (e) {
      return rejectWithValue({
        positionId,
        message: e.response?.data?.message || 'Error moving position',
      })
    }
  },
)

const initialState = positionsAdapter.getInitialState({ loading: 'idle', error: null })

const positionsSlice = createSlice({
  name: 'crmPositions',
  initialState,
  reducers: {
    optimisticMove: (state, action) => {
      const { positionId, stageId } = action.payload
      const p = state.entities[positionId]
      if (p) p.stageId = stageId
    },
    rollbackMove: (state, action) => {
      const { positionId, previousStageId } = action.payload
      const p = state.entities[positionId]
      if (p) p.stageId = previousStageId
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPositions.fulfilled, (state, action) => {
        positionsAdapter.upsertMany(state, action.payload)
      })
      .addCase(fetchPositionsByDeal.fulfilled, (state, action) => {
        positionsAdapter.upsertMany(state, action.payload)
      })
      .addCase(createPosition.fulfilled, (state, action) => {
        positionsAdapter.upsertOne(state, action.payload)
      })
      .addCase(updatePosition.fulfilled, (state, action) => {
        positionsAdapter.upsertOne(state, action.payload)
      })
      .addCase(removePosition.fulfilled, (state, action) => {
        positionsAdapter.removeOne(state, action.payload)
      })
      .addCase(movePosition.fulfilled, (state, action) => {
        positionsAdapter.upsertOne(state, action.payload)
      })
  },
})

export const { optimisticMove: positionsOptimisticMove, rollbackMove: positionsRollbackMove } = positionsSlice.actions

const selectors = positionsAdapter.getSelectors((state) => state.crmPositions)
export const selectAllPositions = selectors.selectAll
export const selectPositionById = selectors.selectById

export const selectPositionsByDeal = (dealId) =>
  createSelector([selectAllPositions], (positions) =>
    positions.filter((p) => p.dealId === dealId),
  )

export const selectPositionsByPipeline = (pipelineId) =>
  createSelector([selectAllPositions], (positions) =>
    positions.filter((p) => p.pipelineId === pipelineId),
  )

// ─────────── Value math (kept here so the UI doesn't reach into services) ───
// All helpers multiply by `quantity` so a position representing N seats of
// the same role (e.g. 3× Fullstack) is reflected directly.
const HOURS_PER_MONTH_DEFAULT = 168

const seatMonthlyRevenue = (p) => {
  const hours = p.commitmentHoursPerMonth || HOURS_PER_MONTH_DEFAULT
  return p.rateBilledUnit === 'hour' ? (p.rateBilled || 0) * hours : p.rateBilled || 0
}

const seatMonthlyCost = (p) => {
  const hours = p.commitmentHoursPerMonth || HOURS_PER_MONTH_DEFAULT
  return p.rateBudgetUnit === 'hour' ? (p.rateBudget || 0) * hours : p.rateBudget || 0
}

const getQuantity = (p) => (p && p.quantity && p.quantity > 0 ? p.quantity : 1)

export function positionMonthlyValue(p) {
  if (!p) return 0
  return seatMonthlyRevenue(p) * getQuantity(p)
}

export function positionTotalValue(p) {
  if (!p) return 0
  return positionMonthlyValue(p) * (p.durationMonths || 1)
}

export function positionMonthlyMargin(p) {
  if (!p) return 0
  return (seatMonthlyRevenue(p) - seatMonthlyCost(p)) * getQuantity(p)
}

export const selectDealPositionSummary = (dealId) =>
  createSelector([selectPositionsByDeal(dealId)], (positions) => {
    const active = positions.filter((p) => ['open', 'paused', 'filled'].includes(p.status))
    return {
      totalValue: active.reduce((s, p) => s + positionTotalValue(p), 0),
      totalMonthlyValue: active.reduce((s, p) => s + positionMonthlyValue(p), 0),
      totalMonthlyMargin: active.reduce((s, p) => s + positionMonthlyMargin(p), 0),
      activeCount: active.length,
      totalCount: positions.length,
      byStatus: positions.reduce((acc, p) => {
        acc[p.status] = (acc[p.status] || 0) + 1
        return acc
      }, {}),
    }
  })

export default positionsSlice.reducer
